import {Dispatch, SetStateAction, useCallback, useEffect, useRef} from 'react';
import {useSearch, useSearchParams} from 'wouter';

import {NumberBrand, StringBrand, asDate, asNumber} from '@shared/lib/utils/type_utils';

import {ReadOnlyRefObject} from '@shared_web/lib/react';
import {useStateRef} from '@shared_web/lib/use_state_ref';

type UseQueryStringReturnType<T> = [T, Dispatch<SetStateAction<T>>, ReadOnlyRefObject<T>];

export function useStringQueryString<T extends StringBrand | string = string>(
  key: string,
  initial: string
): UseQueryStringReturnType<T> {
  const serialize = useCallback((v: T) => v, []);
  const deserialize = useCallback((v: string) => v as T, []);
  return useGenericQueryString(key, initial as T, serialize, deserialize);
}
export function useOptionalStringQueryString<T extends StringBrand | string = string>(
  key: string,
  initial?: T | undefined
): UseQueryStringReturnType<T | undefined> {
  const serialize = useCallback((v: T) => v, []);
  const deserialize = useCallback((v: string) => v as T, []);
  return useGenericOptionalQueryString(key, initial, serialize, deserialize);
}

export function useNumberQueryString<T extends NumberBrand | number = number>(
  key: string,
  initial: number
): UseQueryStringReturnType<T> {
  const deserialize = useCallback((v: string) => asNumber<T>(v, initial), [initial]);
  return useGenericQueryString(key, initial as T, String, deserialize);
}
export function useOptionalNumberQueryString<T extends NumberBrand | number = number>(
  key: string,
  initial?: T | undefined
): UseQueryStringReturnType<T | undefined> {
  const deserialize = useCallback((v: string) => asNumber<T>(v, initial as T), [initial]);
  return useGenericOptionalQueryString(key, initial, String, deserialize);
}

export function useStringArrayQueryString<T extends StringBrand | string = string>(
  key: string,
  initial: T[]
): UseQueryStringReturnType<T[]> {
  const serialize = useCallback((arr: T[]) => arr.join(','), []);
  const deserialize = useCallback((v: string) => (v.length === 0 ? [] : v.split(',')) as T[], []);
  return useGenericQueryString(key, initial as T[], serialize, deserialize);
}

export function useOptionalStringArrayQueryString<T extends StringBrand | string = string>(
  key: string,
  initial?: T[] | undefined
): UseQueryStringReturnType<T[] | undefined> {
  const serialize = useCallback((arr: T[]) => (arr.length > 0 ? arr.join(',') : undefined), []);
  const deserialize = useCallback(
    (v: string) => (v.length === 0 ? undefined : v.split(',')) as T[],
    []
  );
  return useGenericOptionalQueryString(key, initial, serialize, deserialize);
}

export function useDateQueryString(key: string, initial: Date): UseQueryStringReturnType<Date> {
  const serialize = useCallback((v: Date) => String(v.getTime()), []);
  const deserialize = useCallback((v: string) => asDate(v, initial), [initial]);
  return useGenericQueryString(key, initial, serialize, deserialize);
}

export function useBooleanQueryString(
  key: string,
  initial: boolean
): UseQueryStringReturnType<boolean> {
  const serialize = useCallback((v: boolean) => (v ? '1' : '0'), []);
  const deserialize = useCallback((v: string) => v.length > 0 && v !== '0', []);
  return useGenericQueryString(key, initial, serialize, deserialize);
}

export function useOptionalBooleanQueryString(
  key: string,
  initial?: boolean | undefined
): UseQueryStringReturnType<boolean | undefined> {
  const serialize = useCallback((v: boolean) => (v ? '1' : '0'), []);
  const deserialize = useCallback((v: string) => v.length > 0 && v !== '0', []);
  return useGenericOptionalQueryString(key, initial, serialize, deserialize);
}

////////////////

function useGenericQueryString<T>(
  key: string,
  initialValue: T,
  serializer: (val: T) => string,
  deserializer: (val: string) => T
): UseQueryStringReturnType<T> {
  return useGenericOptionalQueryString(
    key,
    initialValue,
    serializer,
    deserializer
  ) as UseQueryStringReturnType<T>;
}

function useGenericOptionalQueryString<T>(
  key: string,
  initialValue: T | undefined,
  serializer: (val: T) => string | undefined,
  deserializer: (val: string) => T
): UseQueryStringReturnType<T | undefined> {
  const search = useSearch();
  const [searchParams, setSearchParams] = useSearchParams();

  // const log = (...args: unknown[]) => key === 'typeLabel' && console.log(...args);
  // log('searchParams', {key, initialValue, serialized: serializer(initialValue as any)});

  const initialValueRef = useRef(initialValue);
  useEffect(() => {
    initialValueRef.current = initialValue;
  }, [initialValue]);

  const [state, setState, stateRef] = useStateRef<T | undefined>(() => {
    const curr = searchParams.get(key);
    return curr === null ? initialValueRef.current : deserializer(curr);
  });

  const setQueryString = useCallback(
    (key: string, value: string | undefined) => {
      setSearchParams(
        curr => {
          if (value === undefined) {
            curr.delete(key);
          } else {
            curr.set(key, value);
          }
          return curr;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  // On the first call, check if the value for "key" is not defined.
  // If that's the case and we have a default value, we update the URL
  useEffect(() => {
    const curr = searchParams.get(key);
    if (curr === null && initialValueRef.current !== undefined) {
      setQueryString(key, serializer(initialValueRef.current));
    }
  }, [key, searchParams, serializer, setQueryString]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Every time the location's search changes, we regenerate the state
  useEffect(() => {
    const current = searchParams.get(key) ?? undefined;
    const newVal = current === undefined ? initialValueRef.current : deserializer(current);
    setState(newVal);
  }, [deserializer, key, searchParams, search, setState]);

  const setParam = useCallback(
    (setter: T | undefined | ((current: T | undefined) => T | undefined)) => {
      const stringVal = searchParams.get(key);
      const newVal =
        typeof setter === 'function'
          ? (setter as (current: T | undefined) => T | undefined)(
              stringVal === null ? initialValueRef.current : deserializer(stringVal)
            )
          : setter;
      setQueryString(key, newVal === undefined ? undefined : serializer(newVal));
      setState(newVal);
    },
    [deserializer, key, searchParams, serializer, setQueryString, setState]
  );

  return [state, setParam, stateRef];
}
